import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound, CalendarClock } from "lucide-react";
import { TwoFactorCard } from "@/components/auth/TwoFactorCard";
import { PacketSchedulePanel } from "@/components/PacketSharePanel";
import { SmsAlertsCard } from "@/components/SmsAlertsCard";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const isCustomer = user?.role === "customer";

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name, company: isCustomer ? company : undefined });
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl" data-testid="profile-page">
      <div className="mb-6">
        <div className="tag-mono text-blue-700 mb-1">ACCOUNT</div>
        <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">My Profile</h1>
        <p className="text-slate-600 mt-1">{user?.email} · <span className="capitalize">{user?.role}</span></p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {user?.role === "customer" && (
          <Card className="p-6 md:col-span-2 border-t-4 border-t-fuchsia-500" id="packet" data-testid="profile-packet-section">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-fuchsia-700" /><h2 className="text-lg font-bold font-heading">Insurance agent &amp; packet schedule</h2></div>
              <Button variant="ghost" size="sm" onClick={async () => { await api.post("/onboarding/checklist/dismiss?dismissed=false"); toast.success("Setup checklist restored on your dashboard"); }} data-testid="profile-show-checklist">Show setup checklist</Button>
            </div>
            <PacketSchedulePanel customerId={user.id} />
          </Card>
        )}
        {user?.role === "customer" && <SmsAlertsCard />}
        <TwoFactorCard />
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><UserCog className="h-5 w-5 text-blue-700" /><h2 className="text-lg font-bold font-heading">Details</h2></div>
          <form onSubmit={saveProfile} className="space-y-4">
            <div><Label htmlFor="name">Name</Label><Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></div>
            {isCustomer && <div><Label htmlFor="company">Company</Label><Input id="company" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="profile-company-input" /></div>}
            <div><Label>Email</Label><Input value={user?.email || ""} disabled className="bg-slate-50" /><p className="text-xs text-slate-500 mt-1">Contact your administrator to change your login email.</p></div>
            <Button type="submit" disabled={savingProfile} className="bg-blue-700 hover:bg-blue-800" data-testid="profile-save-button">{savingProfile ? "Saving…" : "Save details"}</Button>
          </form>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><KeyRound className="h-5 w-5 text-blue-700" /><h2 className="text-lg font-bold font-heading">Change password</h2></div>
          <form onSubmit={savePassword} className="space-y-4">
            <div><Label htmlFor="cur">Current password</Label><Input id="cur" type="password" required autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="password-current-input" /></div>
            <div><Label htmlFor="new">New password</Label><Input id="new" type="password" required minLength={8} autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="password-new-input" /></div>
            <div><Label htmlFor="conf">Confirm new password</Label><Input id="conf" type="password" required minLength={8} autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="password-confirm-input" /></div>
            <Button type="submit" disabled={savingPw} className="bg-blue-700 hover:bg-blue-800" data-testid="password-save-button">{savingPw ? "Updating…" : "Update password"}</Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
